import React, {createContext, useContext, useEffect, useState} from 'react';
import {format, parseISO} from 'date-fns';
import {createColumnHelper, getCoreRowModel, Table, useReactTable} from '@tanstack/react-table';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {DashboardData, DashboardInvoiceRow, MessageItem} from '../../types';
import {Format} from '../../../../common/utils';

export type DashboardContext = {
  updateData: () => void
  series: unknown[]
  categories: unknown[]
  table: Table<DashboardInvoiceRow>
  invoicesCount: number
  unreadMessages: MessageItem[] | [],
  loading: boolean
}

export const defaultContext: DashboardContext = {
  updateData: () => {},
  series: [],
  categories: [],
  table: {} as Table<DashboardInvoiceRow>,
  invoicesCount: 0,
  unreadMessages: [],
  loading: true,
};

export type DashboardContextProviderProps = {
  children: React.ReactNode,
}

export const DashboardContext = createContext<DashboardContext>(defaultContext);
DashboardContext.displayName = 'DashboardContext';

export const useDashboardContext = () => useContext(DashboardContext);

export const DashboardContextProvider = ({children}: DashboardContextProviderProps) => {
  const {apiService} = useGlobalContext();
  const globalMessage = useGlobalMessageContext();
  const M = useMessage();
  const initialData = {
    grid: {
      count: 0,
      list: []
    },
    chart: {
      totalAmount: 0,
      list: {}
    },
    unreadMessages: []
  };

  const [data, setData] = useState<DashboardData>(initialData);
  const [loading, setLoading] = useState(true);

  const updateData = async () => {
    try {
      setData(initialData);
      const result = await apiService.getDashboardData();

      if (result.errorCode < 0) {
        globalMessage.open(M.serverError);
      }
      setData(result.body);
    } catch (e) {
      globalMessage.open(M.serverError);
      setData(initialData);
    }
  };

  useEffect(() => {
    setLoading(true);
    updateData()
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const series = Object.values(data.chart?.list);
  const categories = Object.keys(data.chart?.list).map(item => {
    return `${item.slice(3, 5)}-${item.slice(0, 2)}`;
  });
  const unreadMessages = data.unreadMessages;

  const columnHelper = createColumnHelper<DashboardInvoiceRow>();
  const columns = [
    columnHelper.accessor('customerID', {
      cell: info => info.getValue(),
      header: () => {},
    }),
    columnHelper.accessor('customerName', {
      cell: info => info.getValue(),
      header: () => {},
    }),
    columnHelper.accessor('createdAt', {
      cell: info => info.getValue() ? format(parseISO(info.getValue()), 'dd/MM/yyyy') : '',
      header: () => {},
    }),
    columnHelper.accessor('amount', {
      cell: info => Boolean(info) ? Format.displayFloatThousandsWithCommas(info.getValue()) : '',
      header: () => {},
    }),
  ];
  const table = useReactTable({
    data: data.grid?.list,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const context: DashboardContext = {
    ...defaultContext,
    updateData,
    series,
    categories,
    table,
    invoicesCount: data.grid.count,
    unreadMessages,
    loading,
  };

  return (
    <DashboardContext.Provider value={context}>
      {children}
    </DashboardContext.Provider>
  );
};
